/**
 * Global WebSocket provider for daily reports
 * Uses the shared useWebSocket hook for connection management.
 */
import React, { useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DailyReportsWebSocketContext } from './DailyReportsContext';
import type { DailyReportsWebSocketContextValue } from './DailyReportsContext';
import { useWebSocket } from '../hooks/useWebSocket';
import { QUERY_META } from '../queries/core/queryMeta';
import { receptionKeys } from '../queries/reception/receptionKeys';
import { fetchReceptionDailyReportsWsToken } from '../api/realtime';
import { fetchDailyReportsUnreadCount } from '../queries/reception/receptionQueryFns';
import { useReceptionWorkflowScope } from '../pages/reception/useReceptionWorkflowScope';

interface DailyReportEvent {
  event: string;
  report_id?: number;
  employee_name?: string;
  [key: string]: unknown;
}

interface DailyReportsWebSocketProviderProps {
  children: React.ReactNode;
  enabled?: boolean;
}

const ScopedDailyReportsSocket: React.FC<{
  onConnectionChange: (scopeToken: string, connected: boolean) => void;
  onEvent: (scopeToken: string, event: DailyReportEvent) => void;
  onOpen: (scopeToken: string) => void;
  scopeToken: string;
}> = ({ onConnectionChange, onEvent, onOpen, scopeToken }) => {
  const getToken = useCallback(async () => {
    return fetchReceptionDailyReportsWsToken();
  }, []);

  const { connected } = useWebSocket('/reception/ws/daily-reports', {
    enabled: true,
    maxAttempts: 8,
    getToken,
    onOpen: () => onOpen(scopeToken),
    onMessage: (_raw, parsed) => {
      if (!parsed || typeof parsed !== 'object') return;
      const event = parsed as DailyReportEvent;
      if (event.event === 'hello' || event.event === 'ping') return;
      if (event.event === 'new_daily_report' || event.event === 'report_read') {
        onEvent(scopeToken, event);
      }
    },
  });

  React.useEffect(() => {
    onConnectionChange(scopeToken, connected);
    return () => onConnectionChange(scopeToken, false);
  }, [connected, onConnectionChange, scopeToken]);

  return null;
};

export const DailyReportsWebSocketProvider: React.FC<DailyReportsWebSocketProviderProps> = ({
  children, 
  enabled = false 
}) => {
  const queryClient = useQueryClient();
  const scopeQuery = useReceptionWorkflowScope(enabled);
  const scopeToken = scopeQuery.isSuccess ? scopeQuery.scopeToken : null;
  const activeScopeTokenRef = useRef(scopeToken);
  activeScopeTokenRef.current = scopeToken;
  const subscribersRef = useRef<Set<(event: DailyReportEvent) => void>>(new Set());
  const [connectedScope, setConnectedScope] = React.useState<string | null>(null);
  const unreadCountQueryKey = [
    ...receptionKeys.dailyReports.unreadCount(),
    scopeToken ?? 'unresolved',
  ] as const;
  const unreadCountQuery = useQuery({
    queryKey: unreadCountQueryKey,
    queryFn: fetchDailyReportsUnreadCount,
    enabled: Boolean(enabled && scopeToken),
    retry: 0,
    refetchOnWindowFocus: false,
    meta: QUERY_META.sensitive,
  });
  const unreadCount = unreadCountQuery.data;
  const refetchUnreadCount = unreadCountQuery.refetch;

  const fetchUnreadCount = useCallback(async () => {
    if (!activeScopeTokenRef.current) return;
    await refetchUnreadCount();
  }, [refetchUnreadCount]);

  const subscribe = useCallback((callback: (event: DailyReportEvent) => void) => {
    subscribersRef.current.add(callback);
    return () => { subscribersRef.current.delete(callback); };
  }, []);

  const handleConnectionChange = useCallback((candidateScopeToken: string, connected: boolean) => {
    if (activeScopeTokenRef.current !== candidateScopeToken) return;
    setConnectedScope(connected ? candidateScopeToken : null);
  }, []);

  const handleOpen = useCallback((candidateScopeToken: string) => {
    if (activeScopeTokenRef.current === candidateScopeToken) {
      void refetchUnreadCount();
    }
  }, [refetchUnreadCount]);

  const handleEvent = useCallback((candidateScopeToken: string, event: DailyReportEvent) => {
    if (activeScopeTokenRef.current !== candidateScopeToken) return;
    subscribersRef.current.forEach(callback => {
      try { callback(event); } catch { /* subscriber error */ }
    });
    // WebSocket payloads are freshness hints only. Refetch every active cache
    // owned by this exact principal/workflow token; never mutate counts or
    // invalidate another token's inactive cache.
    void queryClient.invalidateQueries({
      queryKey: receptionKeys.dailyReports.all(),
      predicate: (query) => query.queryKey.at(-1) === candidateScopeToken,
      refetchType: 'active',
    });
  }, [queryClient]);

  const connectionState = !enabled || !scopeToken
    ? 'closed'
    : connectedScope === scopeToken ? 'open' : 'connecting';
  const value = React.useMemo<DailyReportsWebSocketContextValue>(() => ({
    unreadCount: scopeToken ? (unreadCount ?? 0) : 0,
    connectionState,
    subscribe,
    refreshCount: fetchUnreadCount,
  }), [connectionState, fetchUnreadCount, scopeToken, subscribe, unreadCount]);

  return (
    <DailyReportsWebSocketContext.Provider value={value}>
      {enabled && scopeToken ? (
        <ScopedDailyReportsSocket
          key={scopeToken}
          scopeToken={scopeToken}
          onConnectionChange={handleConnectionChange}
          onEvent={handleEvent}
          onOpen={handleOpen}
        />
      ) : null}
      {children}
    </DailyReportsWebSocketContext.Provider>
  );
};
